/**
 * 海域図 API（/api/sea-map/*。担当: G4）
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type CoverageSource = '事前連絡' | '実績';
export type FindingKind = '視認' | '漁具';

export interface CoverageRowDto {
  key: string;
  businessOperatorId: number | null;
  businessOperatorName: string;
  vesselId: number | null;
  vesselName: string;
  nameKana: string;
  ownerName: string;
  captainName: string;
  registrationNo: string;
  tonnage: number | null;
  crewCount: number;
  phone: string;
  totalBoardingCount: number | null;
  departureDate: string;
  arrivalDate: string;
  zoneNumbers: string;
  catchSurveyPlanned: boolean | null;
  catchSalePlanned: boolean | null;
  actualWorkDays: number | null;
  catchSurveyStatus: string | null;
  note: string | null;
  noticeId: number | null;
  voyageReportId: number | null;
}

export interface ZoneCountDto {
  zoneNo: number;
  count: number;
  vesselIds: number[];
}

export interface CoverageDto {
  zoneCounts: ZoneCountDto[];
  rows: CoverageRowDto[];
}

export interface FindingRowDto {
  id: number;
  kind: FindingKind;
  date: string;
  time: string | null;
  monitoringArea: string | null;
  zoneNo: number | null;
  latLon: string;
  activity: string | null;
  heading: string | null;
  foreignVesselName: string | null;
  foreignRegistrationNo: string | null;
  approvalNo: string | null;
  fishingType: string | null;
  vesselCount: number | null;
  isReported: boolean | null;
  gearType: string | null;
  gearCount: string | null;
  mmsi: string | null;
  gearFeatures: string | null;
  nationality: string | null;
  hasPhoto: boolean;
  specialNotes: string | null;
  businessOperatorId: number;
  businessOperatorName: string;
  vesselId: number;
  vesselName: string;
  tonnage: number | null;
  ownerName: string;
  captainName: string;
  voyageReportId: number | null;
  fixedTimeReportId: number | null;
}

export interface FindingsDto {
  zoneCounts: ZoneCountDto[];
  totalCount: number;
  totalVesselCount: number;
  rows: FindingRowDto[];
}

export interface CoverageFilter {
  source: CoverageSource;
  from: string;
  to: string;
  businessOperatorIds: number[];
  zoneNos: number[];
}

export interface FindingsFilter {
  kind: FindingKind;
  from: string;
  to: string;
  months: number[];
  businessOperatorIds: number[];
}

export const seaMapKeys = {
  all: ['sea-map'] as const,
  coverage: (year: number, f: CoverageFilter) => [...seaMapKeys.all, 'coverage', year, f] as const,
  findings: (year: number, f: FindingsFilter) => [...seaMapKeys.all, 'findings', year, f] as const,
};

export function useCoverage(year: number, f: CoverageFilter, enabled = true) {
  return useQuery({
    queryKey: seaMapKeys.coverage(year, f),
    queryFn: ({ signal }) => api.get<CoverageDto>('/sea-map/coverage', { fiscalYear: year, ...f }, { signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useFindings(year: number, f: FindingsFilter, enabled = true) {
  return useQuery({
    queryKey: seaMapKeys.findings(year, f),
    queryFn: ({ signal }) => api.get<FindingsDto>('/sea-map/sightings', { fiscalYear: year, ...f }, { signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/** 'a,b,c' → number[]（不正値は除外） */
export function parseIdList(text: string | null | undefined): number[] {
  if (!text) return [];
  return [
    ...new Set(
      text
        .normalize('NFKC')
        .split(/[,.、・\s]+/)
        .map((s) => s.trim())
        .filter((s) => /^\d+$/.test(s))
        .map(Number),
    ),
  ];
}
